import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Flex, Text, toast, Card } from "@jsq/ds/core";
import { FormTextInput, SubmitButton } from "@jsq/ds/forms";
import { useGetPaymentBatchByIdLazyQuery } from "~/graphql/supergraphHooks";
import { getErrorMessage } from "~/utils/errors";
import PaymentBatchCard from "./PaymentBatchCard";
import PaymentCard from "./PaymentCard";
import * as styles from "./styles_vanilla.css";

interface FormValues {
  paymentBatchId: string;
}

// UUID validation regex
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const validationSchema = Yup.object().shape({
  paymentBatchId: Yup.string()
    .required("Payment batch UUID is required")
    .matches(UUID_REGEX, "Please enter a valid UUID format"),
});

const initialValues: FormValues = {
  paymentBatchId: "",
};

const QueryPaymentBatch: React.FC = () => {
  const [getPaymentBatch, { data, loading }] = useGetPaymentBatchByIdLazyQuery();

  const handleSubmit = async (values: FormValues) => {
    try {
      const result = await getPaymentBatch({
        variables: { id: values.paymentBatchId },
      });

      if (result.error) {
        toast.error(
          getErrorMessage(result.error) || "Payment batch not found or an error occurred",
        );
      } else {
        toast.success("Payment batch found successfully");
      }
    } catch (err) {
      toast.error(getErrorMessage(err) || "An error occurred while querying the payment batch");
    }
  };

  const paymentBatch = data?.paymentBatch;

  return (
    <Flex
      data-testid="query-payment-batch-root"
      flexDirection="column"
      className={styles.container}
    >
      <div className={styles.header}>
        <Text variant="regular-xxxl" className={styles.pageTitle}>
          Query Payment Batch
        </Text>
      </div>

      <div className={styles.contentArea} data-testid="query-payment-batch-content">
        <Card className={styles.formCard}>
          <Text variant="regular-lg" className={styles.formTitle}>
            Enter Payment Batch UUID
          </Text>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) => (
              <Form className={styles.form}>
                <FormTextInput
                  name="paymentBatchId"
                  label="Payment Batch UUID"
                  placeholder="e.g., 123e4567-e89b-12d3-a456-426614174000"
                  className={styles.textInput}
                />
                <SubmitButton
                  isLoading={loading || isSubmitting}
                  className={styles.submitButton}
                >
                  Query Payment Batch
                </SubmitButton>
              </Form>
            )}
          </Formik>
        </Card>

        {paymentBatch && (
          <div className={styles.resultsSection} data-testid="query-results">
            <Text variant="regular-xl" className={styles.resultsTitle}>
              Query Results
            </Text>
            <div className={styles.cardsContainer}>
              <PaymentBatchCard paymentBatch={paymentBatch} />
              {paymentBatch?.fundingPayment && (
                <PaymentCard
                  payment={paymentBatch.fundingPayment}
                  cardTitle="Funding Payment"
                />
              )}
              {paymentBatch?.payments &&
                paymentBatch.payments.length > 0 &&
                paymentBatch.payments.map((payment, index) => (
                  <PaymentCard
                    key={payment.id}
                    payment={payment}
                    cardTitle="Payment"
                    paymentIndex={index + 1}
                  />
                ))}
            </div>
          </div>
        )}
      </div>
    </Flex>
  );
};

export default QueryPaymentBatch;
export { QueryPaymentBatch };
